import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, money, EmptyState, useUrlSearch, SearchInput, matchText } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  format, parseISO, startOfWeek, addDays, addWeeks, addMonths, isSameDay, isSameMonth,
} from "date-fns";
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight, List, CalendarDays, Clock, CalendarRange, MessageSquare, Upload, CheckCircle2, Camera, Play, AlertTriangle } from "lucide-react";
import { jobToEvent, STATUSES, STATUS_LABELS } from "@/lib/schedule";
import { TimeGrid } from "@/components/schedule/TimeGrid";
import { MonthGrid } from "@/components/schedule/MonthGrid";
import { useSettings } from "@/context/SettingsContext";
import ImportJobsDialog from "./ImportJobsDialog";
import ChangeNotifyDialog from "@/components/sms/ChangeNotifyDialog";
import JobPhotosDialog from "@/components/photos/JobPhotosDialog";
import JobPhotos from "@/components/photos/JobPhotos";
import CheckInPanel from "@/components/reports/CheckInPanel";
import QuickClientDialog from "@/components/QuickClientDialog";
import TodayJobs from "@/components/jobs/TodayJobs";
import EditScopeDialog from "@/components/jobs/EditScopeDialog";
import PauseSeriesDialog from "@/components/jobs/PauseSeriesDialog";
import WalkthroughDialog from "@/components/leads/WalkthroughDialog";
import { getPosition } from "@/lib/geo";

function parseTimeToHours(t, fallback) {
  if (!t || !/^\d{1,2}:\d{2}$/.test(t)) return fallback;
  const [h, m] = t.split(":").map(Number);
  return h + m / 60;
}

function label12(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

function strToMin(s) {
  if (!s || !/^\d{1,2}:\d{2}$/.test(s)) return null;
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
}

const emptyForm = {
  customer_id: "", package_id: "", assigned_team_ids: [],
  date: "", time: "09:00", duration_hours: "", rate_per_hour: "", price: "", discount_percent: "",
  status: "scheduled", address: "", notes: "",
  recurrence: "none", recurrence_interval: 2, recurrence_end_type: "count",
  recurrence_count: 4, recurrence_end_date: "",
};

const DURATION_OPTS = Array.from({ length: 16 }, (_, i) => {
  const h = (i + 1) * 0.5;
  const hrs = Math.floor(h);
  const mins = h % 1 ? 30 : 0;
  const label = [hrs ? `${hrs} hr` : "", mins ? `${mins} min` : ""].filter(Boolean).join(" ");
  return { v: String(h), l: label };
});

const freqOf = (j) => ({
  recurrence: j.recurrence || "none",
  recurrence_interval: j.recurrence_interval || 2,
  recurrence_end_type: j.recurrence_end_type || "count",
  recurrence_count: j.recurrence_count || 4,
  recurrence_end_date: j.recurrence_end_date ? j.recurrence_end_date.slice(0, 10) : "",
});
const freqKey = (f) => [f.recurrence, f.recurrence === "custom" ? Number(f.recurrence_interval) : "",
  f.recurrence === "none" ? "" : f.recurrence_end_type,
  f.recurrence === "none" ? "" : (f.recurrence_end_type === "count" ? Number(f.recurrence_count) : f.recurrence_end_date)].join("|");

const RECURRENCE_OPTS = [
  { v: "none", l: "One-off (no repeat)" },
  { v: "weekly", l: "Weekly" },
  { v: "fortnightly", l: "Fortnightly" },
  { v: "monthly", l: "Monthly" },
  { v: "custom", l: "Custom (every N weeks)" },
];

export default function Jobs() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { settings } = useSettings();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [team, setTeam] = useState([]);
  const [view, setView] = useState(() =>
    new URLSearchParams(window.location.search).get("q") ? "list" : "day");
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [monthDate, setMonthDate] = useState(new Date());
  const [search, setSearch] = useUrlSearch();
  const [cleaner, setCleaner] = useState("");
  const [showCancelled, setShowCancelled] = useState(() => localStorage.getItem("showCancelled") === "1");
  const toggleCancelled = (v) => { setShowCancelled(v); localStorage.setItem("showCancelled", v ? "1" : "0"); };
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [change, setChange] = useState(null);
  const [photoJob, setPhotoJob] = useState(null);
  const [quickClient, setQuickClient] = useState(false);
  const [series, setSeries] = useState(null);
  const [scopePrompt, setScopePrompt] = useState(null);
  const [pauseJob, setPauseJob] = useState(null);
  const [walkJob, setWalkJob] = useState(null);
  const [notifyClient, setNotifyClient] = useState(true);
  const closeChange = useCallback(() => setChange(null), []);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadJobs = (retry = true) => api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {
    if (retry) return loadJobs(false);
    toast.error("Couldn't load bookings — your bookings are safe. Refresh the page to try again.");
  });
  useEffect(() => {
    loadJobs();
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    if (isAdmin) {
      api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
      api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  // Open a prefilled booking when arriving from a client's "Book" button (?client=<id>)
  useEffect(() => {
    const cid = searchParams.get("client");
    const jid = searchParams.get("job");
    if (searchParams.get("new") && isAdmin) {
      setForm(emptyForm); setEditing(null); setEditingJob(null); setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
      return;
    }
    if (jid && jobs.length > 0) {
      const job = jobs.find((j) => j.id === jid);
      if (job) {
        openJob(job);
        searchParams.delete("job");
        setSearchParams(searchParams, { replace: true });
        return;
      }
    }
    if (!cid || !isAdmin || customers.length === 0) return;
    const cust = customers.find((c) => c.id === cid);
    if (!cust) return;
    setForm({ ...emptyForm, customer_id: cid, address: cust.address || "" });
    setEditing(null);
    setEditingJob(null);
    setOpen(true);
    searchParams.delete("client");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, customers, jobs, isAdmin]);

  const openNew = () => { setForm(emptyForm); setEditing(null); setEditingJob(null); setOpen(true); };
  const pad2 = (n) => String(n).padStart(2, "0");
  const openNewAt = ({ date, startMin, endMin }) => {
    const dur = Math.min(8, Math.max(0.5, Math.round(((endMin - startMin) / 60) * 2) / 2));
    setForm({ ...emptyForm, date: format(date, "yyyy-MM-dd"), time: `${pad2(Math.floor(startMin / 60))}:${pad2(startMin % 60)}`,
      duration_hours: String(dur), _slot: true,
      assigned_team_ids: cleaner && cleaner !== "unassigned" ? [cleaner] : [] });
    setEditing(null); setEditingJob(null); setOpen(true);
  };
  const openJob = (j) => (j.kind === "walkthrough" ? setWalkJob(j) : openEdit(j));
  const openEdit = (j) => {
    setForm({
      customer_id: j.customer_id || "",
      package_id: j.package_id || "",
      assigned_team_ids: j.assigned_team_ids || [],
      date: j.scheduled_date ? format(parseISO(j.scheduled_date), "yyyy-MM-dd") : "",
      time: j.scheduled_date ? format(parseISO(j.scheduled_date), "HH:mm") : "09:00",
      duration_hours: j.duration_hours != null ? String(j.duration_hours) : "",
      rate_per_hour: j.rate_per_hour != null ? String(j.rate_per_hour) : "",
      price: j.price || "",
      discount_percent: j.discount_percent || "",
      status: j.status || "scheduled",
      address: j.address || "",
      notes: j.notes || "",
      ...freqOf(j),
    });
    api.get(`/jobs/${j.id}/series`).then((r) => setSeries(r.data)).catch(() => setSeries(null));
    setEditing(j.id);
    setEditingJob(j);
    setOpen(true);
  };

  // price = hourly rate × duration − discount (falls back to existing price if no rate)
  const withPrice = (f) => {
    const rate = Number(f.rate_per_hour) || 0;
    const dur = Number(f.duration_hours) || 0;
    const disc = Number(f.discount_percent) || 0;
    if (rate <= 0 || dur <= 0) return f;
    return { ...f, price: +(rate * dur * (1 - disc / 100)).toFixed(2) };
  };

  const onSelectPackage = (pid) => {
    const p = packages.find((x) => x.id === pid);
    const cust = customers.find((c) => c.id === form.customer_id);
    setForm((f) => {
      const next = {
        ...f,
        package_id: pid,
        duration_hours: p && !f._slot ? String(p.duration_hours) : f.duration_hours,
        rate_per_hour: p ? String(p.rate_per_hour || "") : f.rate_per_hour,
        discount_percent: f.discount_percent || 0,
        address: f.address || cust?.address || "",
      };
      if (p && (!p.rate_per_hour || p.rate_per_hour <= 0)) {
        next.price = p.base_price * (1 - (Number(f.discount_percent) || 0) / 100);
        return next;
      }
      return p ? withPrice(next) : next;
    });
  };

  const onSelectCustomer = (cid) => {
    const cust = customers.find((c) => c.id === cid);
    setForm((f) => ({ ...f, customer_id: cid, address: f.address || cust?.address || "" }));
  };

  // 15-min start-time slots across working hours, flagging slots already booked that day
  const timeSlots = useMemo(() => {
    const start = strToMin(settings?.day_start) ?? 6 * 60;
    const end = strToMin(settings?.day_end) ?? 20 * 60;
    const busy = [];
    if (form.date) {
      for (const j of jobs) {
        if (editing && j.id === editing) continue;
        if (j.status === "cancelled" || !j.scheduled_date) continue;
        const d = parseISO(j.scheduled_date);
        if (format(d, "yyyy-MM-dd") !== form.date) continue;
        const s = d.getHours() * 60 + d.getMinutes();
        busy.push([s, s + Math.round((j.duration_hours || 0) * 60)]);
      }
    }
    const slots = [];
    for (let m = start; m <= end; m += 15) {
      const isBusy = busy.some(([s, e]) => m >= s && m < e);
      slots.push({
        value: `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`,
        label: label12(m),
        isBusy,
      });
    }
    return slots;
  }, [settings, form.date, jobs, editing]);

  const toggleTeam = (tid) => {
    setForm((f) => ({
      ...f,
      assigned_team_ids: f.assigned_team_ids.includes(tid)
        ? f.assigned_team_ids.filter((x) => x !== tid)
        : [...f.assigned_team_ids, tid],
    }));
  };

  const save = async (scope) => {
    if (!form.customer_id) return toast.error("Select a client");
    if (!form.date) return toast.error("Pick a date");
    if (!form.time) return toast.error("Pick a start time");
    const payload = {
      customer_id: form.customer_id,
      package_id: form.package_id || null,
      assigned_team_ids: form.assigned_team_ids,
      scheduled_date: new Date(`${form.date}T${form.time}`).toISOString(),
      duration_hours: Number(form.duration_hours) || 0,
      rate_per_hour: Number(form.rate_per_hour) || 0,
      price: Number(form.price) || 0,
      discount_percent: Number(form.discount_percent) || 0,
      status: form.status,
      address: form.address,
      notes: form.notes,
      recurrence: editing ? "none" : form.recurrence,
      recurrence_interval: Number(form.recurrence_interval) || 1,
      recurrence_end_type: form.recurrence_end_type,
      recurrence_count: Number(form.recurrence_count) || 1,
      recurrence_end_date: form.recurrence_end_date ? new Date(form.recurrence_end_date).toISOString() : null,
    };
    try {
      if (editing) {
        const orig = jobs.find((j) => j.id === editing);
        const freqChanged = orig && freqKey(freqOf(orig)) !== freqKey(form);
        const diff = {};
        if (orig) {
          ["duration_hours", "rate_per_hour", "price", "discount_percent"].forEach((k) => {
            if (Math.abs((Number(payload[k]) || 0) - (Number(orig[k]) || 0)) > 0.001) diff[k] = payload[k];
          });
          ["package_id", "address", "notes"].forEach((k) => {
            if ((payload[k] || "") !== (orig[k] || "")) diff[k] = payload[k];
          });
          const ids = (a) => JSON.stringify([...(a || [])].sort());
          if (ids(payload.assigned_team_ids) !== ids(orig.assigned_team_ids)) diff.assigned_team_ids = payload.assigned_team_ids;
        }
        const shift = orig ? Math.round((new Date(payload.scheduled_date) - new Date(orig.scheduled_date)) / 60000) : 0;
        if (!scope && !freqChanged && series?.future_scheduled > 0 && (Object.keys(diff).length || shift)) {
          setScopePrompt({ future: series.future_scheduled });
          return;
        }
        if (freqChanged && series?.future_scheduled > 0 &&
          !window.confirm(`Changing the frequency will replace ${series.future_scheduled} upcoming scheduled visit${series.future_scheduled === 1 ? "" : "s"} in this series. Completed and cancelled visits are kept. Continue?`)) return;
        await api.put(`/jobs/${editing}`, payload);
        if (freqChanged) {
          const { data } = await api.post(`/jobs/${editing}/frequency`, {
            recurrence: form.recurrence,
            recurrence_interval: Number(form.recurrence_interval) || 1,
            recurrence_end_type: form.recurrence_end_type,
            recurrence_count: Number(form.recurrence_count) || 1,
            recurrence_end_date: form.recurrence_end_date ? new Date(form.recurrence_end_date).toISOString() : null,
          });
          toast.success(`Booking updated · frequency changed (${data.created} new visit${data.created === 1 ? "" : "s"}${data.removed ? `, ${data.removed} replaced` : ""})`);
        } else if (scope === "following") {
          const { data } = await api.post(`/jobs/${editing}/apply-following`, { changes: diff, shift_minutes: shift });
          toast.success(`Updated this booking and ${data.updated} following visit${data.updated === 1 ? "" : "s"}`);
        } else {
          toast.success("Booking updated");
        }
        if (orig && user?.role === "admin") {
          if (payload.status === "cancelled" && orig.status !== "cancelled") {
            setChange({ jobId: editing, kind: "cancelled" });
          } else if (payload.status === "scheduled" && orig.scheduled_date &&
            Math.abs(new Date(orig.scheduled_date) - new Date(payload.scheduled_date)) >= 60000) {
            setChange({ jobId: editing, kind: "rescheduled", previous_date: orig.scheduled_date });
          } else if (payload.status === "scheduled" && Math.abs((Number(payload.duration_hours) || 0) - (Number(orig.duration_hours) || 0)) > 0.01) {
            setChange({ jobId: editing, kind: "duration" });
          }
        }
      } else {
        const { data } = await api.post("/jobs", { ...payload, notify_client: notifyClient });
        const c = data.confirmation;
        const ct = !c ? "" : c.status === "test_mode" ? " · confirmation logged (live sending off)" : c.status === "skipped" ? " · no confirmation (no mobile / prefers email)" : c.error ? " · confirmation failed" : " · confirmation texted";
        toast.success((data.created > 1 ? `${data.created} bookings scheduled` : "Booking scheduled") + ct);
      }
      setOpen(false);
      loadJobs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    await api.delete(`/jobs/${id}`);
    toast.success("Booking deleted");
    loadJobs();
  };

  const updateStatus = async (j, status) => {
    await api.put(`/jobs/${j.id}`, { status });
    window.dispatchEvent(new Event("job-status-changed"));
    if (status === "cancelled" && j.status !== "cancelled" && user?.role === "admin") {
      setChange({ jobId: j.id, kind: "cancelled" });
    }
    loadJobs();
  };

  const resumeSeries = async () => {
    try {
      const { data } = await api.post(`/jobs/${editing}/resume`);
      toast.success(`${data.resumed} visit${data.resumed === 1 ? "" : "s"} resumed`);
      setOpen(false);
      loadJobs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const startJob = async (j) => {
    try {
      const geo = await getPosition();
      await api.put(`/jobs/${j.id}`, { status: "in_progress", ...(geo || {}) });
      toast.success("Job started — your location is shared with the office until you mark it complete");
      window.dispatchEvent(new Event("job-status-changed"));
      loadJobs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const markComplete = (j) => {
    if (["scheduled", "in_progress"].includes(j.status)) setPhotoJob({ job: j, mode: "complete" });
    else setPhotoJob({ job: j, mode: "photos" });
  };

  const sendReminder = async () => {
    if (!editing) return;
    try {
      const { data } = await api.post(`/jobs/${editing}/send-reminder`);
      toast.success(data.test_mode ? "Reminder logged in SMS test mode (live sending is off)" : "Reminder sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      try {
        const { data } = await api.get("/jobs");
        setJobs(data);
        const found = data.find((j) => j.id === editing);
        if (found) setEditingJob(found);
      } catch (_) {}
    }
  };

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const fjobs = useMemo(() => jobs.filter((j) => {
    if (!cleaner) return true;
    const ids = j.assigned_team_ids || [];
    return cleaner === "unassigned" ? ids.length === 0 : ids.includes(cleaner);
  }), [jobs, cleaner]);

  const periodJobs = fjobs.filter((j) => {
    if (!j.scheduled_date || j.status === "cancelled" || j.status === "paused") return false;
    const d = parseISO(j.scheduled_date);
    if (view === "day") return isSameDay(d, selectedDay);
    if (view === "week") return d >= weekStart && d < addDays(weekStart, 7);
    if (view === "month") return isSameMonth(d, monthDate);
    return true;
  });
  const periodRevenue = periodJobs.reduce((sum, j) => sum + (Number(j.price) || 0), 0);
  const periodLabel = { day: format(selectedDay, "EEE d MMM"), week: "this week view", month: format(monthDate, "MMMM yyyy"), list: "all bookings" }[view];

  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const unfinished = jobs.filter((j) => j.status === "in_progress" && j.scheduled_date && parseISO(j.scheduled_date) < todayStart);

  const sortedJobs = [...fjobs].sort((a, b) =>
    (a.scheduled_date || "").localeCompare(b.scheduled_date || ""));

  const eventsFor = (day) =>
    fjobs
      .filter((j) => j.scheduled_date && (showCancelled || j.status !== "cancelled") && isSameDay(parseISO(j.scheduled_date), day))
      .map(jobToEvent);

  const bizStart = useMemo(() => Math.floor(parseTimeToHours(settings?.day_start, 8)), [settings]);

  // Day view — full 24 hours, scrolled to business start
  const dayEvents = useMemo(() => eventsFor(selectedDay), [fjobs, selectedDay, showCancelled]);
  const dayRange = { startH: 0, endH: 24 };

  // Week view (hourly grid) — shared range across the week
  const weekColumns = useMemo(
    () => weekDays.map((d) => ({
      key: d.toISOString(),
      date: d,
      events: eventsFor(d),
      header: (
        <div>
          <div className="label-caps text-muted-foreground">{format(d, "EEE")}</div>
          <div className={`font-serif text-xl ${isSameDay(d, new Date()) ? "text-brand" : "text-foreground"}`}>{format(d, "d")}</div>
        </div>
      ),
    })),
    [fjobs, weekDays, showCancelled]
  );
  const weekRange = { startH: 0, endH: 24 };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <PageHeader
        eyebrow="Bookings & Scheduling"
        title="Schedule"
        description={isAdmin ? "Assign teams to cleans and keep every booking in view." : "Your assigned cleans."}
        action={
          isAdmin && (
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-jobs-button" className="rounded-sm gap-2">
                <Upload className="w-4 h-4" /> Import CSV
              </Button>
              <Button onClick={openNew} data-testid="add-job-button" className="rounded-sm gap-2">
                <Plus className="w-4 h-4" /> New Booking
              </Button>
            </div>
          )
        }
      />

      {!isAdmin && <TodayJobs jobs={jobs} onChanged={loadJobs} />}
      {isAdmin && unfinished.length > 0 && (
        <div className="mb-6 rounded-xl border border-destructive/50 bg-destructive/5 p-4" data-testid="unfinished-jobs-alert">
          <div className="flex items-center gap-2 text-sm font-medium text-destructive">
            <AlertTriangle className="w-4 h-4" /> {unfinished.length} job{unfinished.length === 1 ? " was" : "s were"} started but not finished
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {unfinished.map((j) => (
              <button key={j.id} type="button" onClick={() => openEdit(j)} data-testid={`unfinished-job-${j.id}`}
                className="text-xs rounded-full border border-destructive/40 px-3 py-1 hover:bg-destructive/10">
                {format(parseISO(j.scheduled_date), "EEE d MMM")} · {j.customer?.name || "Client"} · {(j.team || []).map((t) => t.name).join(", ") || "—"}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">Open a booking to set the actual finish time (Check-in log → Correct times) and mark it completed.</p>
        </div>
      )}

      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex gap-1 border border-border/60 rounded-sm p-1">
          <button onClick={() => setView("day")} data-testid="view-day" className={`px-3 sm:px-4 py-2 text-sm flex items-center gap-1.5 sm:gap-2 rounded-sm ${view === "day" ? "bg-[hsl(330_85%_93%)] grad-ink" : "text-muted-foreground hover:bg-accent/60"}`}>
            <Clock className="w-4 h-4" /> <span className="gi-text">Day</span>
          </button>
          <button onClick={() => setView("week")} data-testid="view-week" className={`px-3 sm:px-4 py-2 text-sm flex items-center gap-1.5 sm:gap-2 rounded-sm ${view === "week" ? "bg-[hsl(330_85%_93%)] grad-ink" : "text-muted-foreground hover:bg-accent/60"}`}>
            <CalendarDays className="w-4 h-4" /> <span className="gi-text">Week</span>
          </button>
          <button onClick={() => setView("month")} data-testid="view-month" className={`px-3 sm:px-4 py-2 text-sm flex items-center gap-1.5 sm:gap-2 rounded-sm ${view === "month" ? "bg-[hsl(330_85%_93%)] grad-ink" : "text-muted-foreground hover:bg-accent/60"}`}>
            <CalendarRange className="w-4 h-4" /> <span className="gi-text">Month</span>
          </button>
          <button onClick={() => setView("list")} data-testid="view-list" className={`px-3 sm:px-4 py-2 text-sm flex items-center gap-1.5 sm:gap-2 rounded-sm ${view === "list" ? "bg-[hsl(330_85%_93%)] grad-ink" : "text-muted-foreground hover:bg-accent/60"}`}>
            <List className="w-4 h-4" /> <span className="gi-text">List</span>
          </button>
        </div>
        {view === "week" && (
          <div className="flex items-center gap-2 sm:gap-4">
            <button onClick={() => setWeekStart(addWeeks(weekStart, -1))} data-testid="week-prev" className="p-2 text-muted-foreground hover:text-foreground">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="font-serif text-lg sm:text-xl">
              {format(weekStart, "d MMM")} – {format(addDays(weekStart, 6), "d MMM yyyy")}
            </span>
            <button onClick={() => setWeekStart(addWeeks(weekStart, 1))} data-testid="week-next" className="p-2 text-muted-foreground hover:text-foreground">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
        {view === "day" && (
          <div className="flex items-center gap-2 sm:gap-4">
            <button onClick={() => setSelectedDay(addDays(selectedDay, -1))} data-testid="day-prev" className="p-2 text-muted-foreground hover:text-foreground">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="font-serif text-lg sm:text-xl" data-testid="day-label">{format(selectedDay, "EEEE, d MMMM yyyy")}</span>
            <button onClick={() => setSelectedDay(addDays(selectedDay, 1))} data-testid="day-next" className="p-2 text-muted-foreground hover:text-foreground">
              <ChevronRight className="w-5 h-5" />
            </button>
            <button onClick={() => setSelectedDay(new Date())} data-testid="day-today" className="text-sm px-3 py-1.5 border border-brand/60 rounded-sm text-muted-foreground hover:text-foreground hover:bg-brand/15">Today</button>
          </div>
        )}
        {view === "month" && (
          <div className="flex items-center gap-4">
            <button onClick={() => setMonthDate(addMonths(monthDate, -1))} data-testid="month-prev" className="p-2 text-muted-foreground hover:text-foreground">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="font-serif text-xl" data-testid="month-label">{format(monthDate, "MMMM yyyy")}</span>
            <button onClick={() => setMonthDate(addMonths(monthDate, 1))} data-testid="month-next" className="p-2 text-muted-foreground hover:text-foreground">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 rounded-sm border border-border/60 bg-[hsl(330_60%_97%)] px-5 py-4" data-testid="cleaner-filter-bar">
          <div className="flex items-center gap-3">
            <span className="label-caps text-muted-foreground">Cleaner</span>
            <select value={cleaner} onChange={(e) => setCleaner(e.target.value)} data-testid="cleaner-filter"
              className="border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand min-w-[200px]">
              <option value="">All cleaners</option>
              {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              <option value="unassigned">Unassigned</option>
            </select>
            {view !== "list" && (
              <label className="flex items-center gap-2 ml-2 text-sm text-muted-foreground cursor-pointer" data-testid="show-cancelled-toggle">
                <Switch checked={showCancelled} onCheckedChange={toggleCancelled} data-testid="show-cancelled-switch" />
                Show cancelled
              </label>
            )}
          </div>
          <div className="text-right" data-testid="cleaner-revenue">
            <div className="text-xs text-muted-foreground">
              {periodJobs.length} booking{periodJobs.length === 1 ? "" : "s"} · {periodLabel}
            </div>
            <div className="font-serif text-2xl text-brand-gradient leading-tight pb-0.5" data-testid="cleaner-revenue-total">
              {money(periodRevenue)} <span className="text-sm font-sans text-muted-foreground">+ GST</span>
            </div>
            <div className="text-xs text-muted-foreground" data-testid="cleaner-revenue-incl">{money(periodRevenue * 1.1)} incl. GST</div>
          </div>
        </div>
      )}

      {(view === "week" || view === "day") && isAdmin && team.length > 0 && !cleaner && (
        <div className="flex flex-wrap gap-4 mb-6" data-testid="team-legend">
          {team.map((m) => (
            <span key={m.id} className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: m.color || "#8a817c" }} />
              {m.name}
            </span>
          ))}
        </div>
      )}

      {view === "day" ? (
        <TimeGrid
          columns={[{ key: "day", date: selectedDay, events: dayEvents, header: null }]}
          onSlotSelect={isAdmin ? openNewAt : undefined}
          range={dayRange}
          showHeader={false}
          initialScrollHour={bizStart}
          onEventClick={(j) => (isAdmin ? openJob(j) : markComplete(j))}
        />
      ) : view === "week" ? (
        <TimeGrid
          columns={weekColumns}
          onSlotSelect={isAdmin ? openNewAt : undefined}
          range={weekRange}
          showHeader
          initialScrollHour={bizStart}
          onEventClick={(j) => (isAdmin ? openJob(j) : markComplete(j))}
        />
      ) : view === "month" ? (
        <MonthGrid
          monthDate={monthDate}
          jobs={fjobs}
          onDayClick={(d) => { setSelectedDay(d); setView("day"); }}
          onEventClick={(j) => (isAdmin ? openJob(j) : markComplete(j))}
        />
      ) : sortedJobs.length === 0 ? (
        <EmptyState title="No bookings yet" description="Schedule your first clean to see it here." />
      ) : (
        <>
          <div className="mb-6">
            <SearchInput value={search} onChange={setSearch} placeholder="Search bookings…" testid="jobs-search" />
          </div>
          {sortedJobs.filter((j) => matchText(search, j.customer?.name, j.package?.name, j.status, j.address)).length === 0 ? (
            <EmptyState title="No matches" description={`No bookings match “${search}”.`} />
          ) : (
            <div className="divide-y divide-border/60 border-y border-border/60">
              {sortedJobs
                .filter((j) => matchText(search, j.customer?.name, j.package?.name, j.status, j.address))
                .map((j) => (
            <div key={j.id} className="flex items-center justify-between py-5" data-testid={`job-row-${j.id}`}>
              <div className="flex items-baseline gap-6">
                <div className="font-serif text-lg w-44 shrink-0">
                  {j.scheduled_date ? format(parseISO(j.scheduled_date), "EEE d MMM yyyy · h:mma") : "—"}
                </div>
                <div>
                  <div className="font-medium">{j.customer?.name || "Unknown"}</div>
                  <div className="text-sm text-muted-foreground">
                    {j.kind === "walkthrough" ? <span className="text-[#0f766e] font-medium">Free walk-through</span> : <>{j.package?.name || "Custom clean"}{j.price != null && <> · {money(j.price)}</>}</>}
                    {j.team?.length ? ` · ${j.team.map((t) => t.name).join(", ")}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {user?.role === "staff" && (
                  <Button variant="outline" onClick={() => setPhotoJob({ job: j, mode: "photos" })} data-testid={`job-photos-${j.id}`} className="rounded-sm gap-2">
                    <Camera className="w-4 h-4" /> Photos{j.photos?.length ? ` (${j.photos.length})` : ""}
                  </Button>
                )}
                {user?.role === "staff" && j.status === "scheduled" && (
                  <Button variant="outline" onClick={() => startJob(j)} data-testid={`start-job-${j.id}`} className="rounded-sm gap-2">
                    <Play className="w-4 h-4" /> Start job
                  </Button>
                )}
                {user?.role === "staff" && ["scheduled", "in_progress"].includes(j.status) && (
                  <Button onClick={() => markComplete(j)} data-testid={`mark-complete-${j.id}`} className="rounded-sm gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Mark complete
                  </Button>
                )}
                {user?.role === "staff" ? (
                  <Select value={j.status} onValueChange={(v) => updateStatus(j, v)}>
                    <SelectTrigger className="w-40 rounded-sm" data-testid={`job-status-${j.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <StatusBadge status={j.status} />
                )}
                {isAdmin && (
                  <>
                    <button onClick={() => openJob(j)} data-testid={`edit-job-${j.id}`} className="p-2 text-muted-foreground hover:text-primary">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(j.id)} data-testid={`delete-job-${j.id}`} className="p-2 text-muted-foreground hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
            </div>
          )}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing ? "Edit Booking" : "New Booking"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="label-caps text-muted-foreground">Client</Label>
                {!editing && (
                  <button type="button" onClick={() => setQuickClient(true)} data-testid="job-new-client"
                    className="text-xs inline-flex items-center gap-1 text-brand hover:underline">
                    <Plus className="w-3 h-3" /> New client
                  </button>
                )}
              </div>
              <Select value={form.customer_id} onValueChange={onSelectCustomer}>
                <SelectTrigger className="rounded-sm" data-testid="job-customer">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Package</Label>
              <Select value={form.package_id} onValueChange={onSelectPackage}>
                <SelectTrigger className="rounded-sm" data-testid="job-package">
                  <SelectValue placeholder="Select package (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {packages.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="job-date-trigger"
                      className="w-full justify-start rounded-sm font-normal gap-2"
                    >
                      <CalendarDays className="w-4 h-4 text-muted-foreground" />
                      {form.date ? format(parseISO(form.date), "EEE d MMM yyyy") : <span className="text-muted-foreground">Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.date ? parseISO(form.date) : undefined}
                      onSelect={(d) => d && setForm((f) => ({ ...f, date: format(d, "yyyy-MM-dd") }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Start time</Label>
                <Select value={form.time} onValueChange={(v) => setForm((f) => ({ ...f, time: v }))}>
                  <SelectTrigger data-testid="job-time" className="gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
                    <SelectValue placeholder="Select a time" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {form.time && !timeSlots.some((s) => s.value === form.time) && (
                      <SelectItem value={form.time}>{label12(strToMin(form.time) ?? 0)}</SelectItem>
                    )}
                    {timeSlots.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        <span className={s.isBusy ? "text-muted-foreground" : ""}>
                          {s.label}{s.isBusy ? "  ·  busy" : ""}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Duration</Label>
                <Select value={form.duration_hours ? String(form.duration_hours) : ""} onValueChange={(v) => setForm((f) => withPrice({ ...f, duration_hours: v }))}>
                  <SelectTrigger data-testid="job-duration"><SelectValue placeholder="Select duration" /></SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTS.map((o) => (
                      <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Hourly rate (AUD)</Label>
                <Input type="number" value={form.rate_per_hour} onChange={(e) => setForm((f) => withPrice({ ...f, rate_per_hour: e.target.value }))} data-testid="job-rate" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Price (AUD)</Label>
                <Input type="number" value={form.price} onChange={set("price")} data-testid="job-price" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-muted-foreground">Discount %</Label>
                <Input type="number" value={form.discount_percent} onChange={(e) => setForm((f) => withPrice({ ...f, discount_percent: e.target.value }))} data-testid="job-discount" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Address</Label>
              <Input value={form.address} onChange={set("address")} data-testid="job-address" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Assign team</Label>
              <div className="border border-border/60 rounded-sm divide-y divide-border/60 max-h-40 overflow-y-auto">
                {team.length === 0 && <p className="text-sm text-muted-foreground p-3">No team members yet.</p>}
                {team.map((m) => (
                  <label key={m.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-accent/40" data-testid={`job-team-${m.id}`}>
                    <input
                      type="checkbox"
                      checked={form.assigned_team_ids.includes(m.id)}
                      onChange={() => toggleTeam(m.id)}
                      className="accent-primary w-4 h-4"
                    />
                    <span className="text-sm">{m.name}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{m.position || m.title}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-sm" data-testid="job-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div className="space-y-4 border border-border/60 rounded-sm p-4" data-testid="job-frequency-section">
                <div className="space-y-2">
                  <Label className="label-caps text-muted-foreground">{editing ? "Frequency" : "Repeat"}</Label>
                  {editing && (
                    <p className="text-xs text-muted-foreground" data-testid="job-series-info">
                      {series?.series_id
                        ? `Part of a repeating series · ${series.future_scheduled} upcoming scheduled visit${series.future_scheduled === 1 ? "" : "s"} after this one. Changes apply from this booking onwards.`
                        : "One-off booking. Choose a frequency to repeat it from this date."}
                    </p>
                  )}
                  {editing && series?.series_id && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button type="button" size="sm" variant="outline" onClick={() => setPauseJob(editingJob)} data-testid="pause-series-button" className="rounded-sm">Pause series…</Button>
                      {series.paused > 0 && (
                        <Button type="button" size="sm" variant="outline" onClick={resumeSeries} data-testid="resume-series-button" className="rounded-sm">
                          Resume {series.paused} paused visit{series.paused === 1 ? "" : "s"}
                        </Button>
                      )}
                    </div>
                  )}
                  <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="job-recurrence">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RECURRENCE_OPTS.map((o) => (
                        <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.recurrence !== "none" && (
                  <>
                    {form.recurrence === "custom" && (
                      <div className="space-y-2">
                        <Label className="label-caps text-muted-foreground">Every N weeks</Label>
                        <Input type="number" min="1" value={form.recurrence_interval} onChange={set("recurrence_interval")} data-testid="job-recurrence-interval" />
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="label-caps text-muted-foreground">Ends</Label>
                        <Select value={form.recurrence_end_type} onValueChange={(v) => setForm({ ...form, recurrence_end_type: v })}>
                          <SelectTrigger className="rounded-sm" data-testid="job-recurrence-end-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="count">After N visits</SelectItem>
                            <SelectItem value="date">On a date</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {form.recurrence_end_type === "count" ? (
                        <div className="space-y-2">
                          <Label className="label-caps text-muted-foreground">Number of visits</Label>
                          <Input type="number" min="1" value={form.recurrence_count} onChange={set("recurrence_count")} data-testid="job-recurrence-count" />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label className="label-caps text-muted-foreground">End date</Label>
                          <Input type="date" value={form.recurrence_end_date} onChange={set("recurrence_end_date")} data-testid="job-recurrence-end-date" />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label className="label-caps text-muted-foreground">Notes</Label>
              <Textarea value={form.notes} onChange={set("notes")} data-testid="job-notes" />
            </div>
          </div>
          {editing && editingJob && (
            <div className="mt-2 border-t border-border/60 pt-4">
              <div className="label-caps text-muted-foreground mb-3">Check-in log</div>
              <CheckInPanel jobId={editingJob.id} />
            </div>
          )}
          {editing && editingJob && (
            <div className="mt-2 border-t border-border/60 pt-4">
              <div className="label-caps text-muted-foreground mb-3">Job photos</div>
              <JobPhotos job={editingJob} user={user} onChange={() => loadJobs()} />
            </div>
          )}
          {editing && (
            <div className="mt-2 border-t border-border/60 pt-4">
              <div className="label-caps text-muted-foreground mb-3">Reminder history</div>
              {(editingJob?.reminder_log?.length || 0) === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="reminder-log-empty">
                  No reminders sent yet.
                </p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto" data-testid="reminder-log">
                  {[...editingJob.reminder_log].reverse().map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-sm" data-testid={`reminder-log-entry-${i}`}>
                      <span className="text-muted-foreground">
                        {(() => { try { return format(parseISO(r.at), "d MMM yyyy · h:mma"); } catch { return r.at; } })()}
                        <span className="ml-2 label-caps">{r.type}</span>
                      </span>
                      {(r.detail || "").includes("Live sending is off") ? (
                        <span className="text-muted-foreground">Test mode</span>
                      ) : (
                        <span className={r.sent ? "text-[hsl(140_25%_35%)]" : "text-destructive"}>
                          {r.sent ? "Sent" : "Failed"}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {!editing && (
            <label className="flex items-center gap-2 text-sm cursor-pointer -mt-2 mb-2" data-testid="notify-client-toggle">
              <input type="checkbox" checked={notifyClient} onChange={(e) => setNotifyClient(e.target.checked)} className="accent-primary w-4 h-4" data-testid="notify-client-checkbox" />
              Text the client a booking confirmation
            </label>
          )}
          <DialogFooter>
            {editing && (
              <Button variant="outline" onClick={sendReminder} data-testid="send-reminder-button" className="rounded-sm mr-auto gap-2">
                <MessageSquare className="w-4 h-4" /> Send reminder
              </Button>
            )}
            {editing && form.status !== "cancelled" && (
              <Button variant="outline" data-testid="cancel-booking-button" className="rounded-sm text-destructive border-destructive/40 hover:bg-destructive/10"
                onClick={async () => {
                  const j = jobs.find((x) => x.id === editing);
                  if (!j || !window.confirm("Cancel this booking? It will be marked Cancelled and removed from the calendar.")) return;
                  await updateStatus(j, "cancelled");
                  toast.success("Booking cancelled");
                  setOpen(false);
                }}>
                Cancel booking
              </Button>
            )}
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm" data-testid="close-job-dialog">Close</Button>
            <Button onClick={() => save()} data-testid="save-job-button" className="rounded-sm">Save Booking</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportJobsDialog open={importOpen} onOpenChange={setImportOpen} onImported={loadJobs} />
      <ChangeNotifyDialog change={change} onClose={closeChange} />
      <PauseSeriesDialog job={pauseJob} onClose={() => setPauseJob(null)} onPaused={(range) => {
        const jid = pauseJob.id;
        setPauseJob(null); setOpen(false); loadJobs();
        if (range) setChange({ jobId: jid, kind: "paused", pause_start: range.start, pause_end: range.end });
      }} />
      <EditScopeDialog prompt={scopePrompt} onCancel={() => setScopePrompt(null)}
        onChoose={(scope) => { setScopePrompt(null); save(scope); }} />
      <QuickClientDialog open={quickClient} onOpenChange={setQuickClient} onCreated={(c) => {
        setCustomers((prev) => [...prev, c].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        setForm((f) => ({ ...f, customer_id: c.id, address: f.address || [c.address, c.suburb].filter(Boolean).join(", ") }));
      }} />
      <WalkthroughDialog job={walkJob} team={team} onClose={() => setWalkJob(null)} onSaved={loadJobs} />
      <JobPhotosDialog job={photoJob?.job} mode={photoJob?.mode} user={user} onClose={() => setPhotoJob(null)} onDone={loadJobs} />
    </div>
  );
}
